import { useState } from "react";
import { cn } from "../lib/utils";

interface AccordionItem {
  value: string;
  title: string;
  content: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
}

interface AccordionProps {
  items: AccordionItem[];
  defaultOpen?: string[];
  multiple?: boolean;
  className?: string;
  variant?: "default" | "bordered";
}

function ChevronDown() {
  return (
    <svg className="accordion-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

export function Accordion({ items, defaultOpen = [], multiple = false, className, variant = "default" }: AccordionProps) {
  const [open, setOpen] = useState<Set<string>>(new Set(defaultOpen));

  const toggle = (value: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        if (!multiple) next.clear();
        next.add(value);
      }
      return next;
    });
  };

  return (
    <div className={cn("accordion", variant === "bordered" && "accordion-bordered", className)}>
      {items.map((item) => {
        const isOpen = open.has(item.value);
        return (
          <div
            key={item.value}
            className={cn("accordion-item", isOpen && "accordion-open")}
          >
            <button
              className="accordion-trigger"
              disabled={item.disabled}
              aria-expanded={isOpen}
              onClick={() => !item.disabled && toggle(item.value)}
            >
              {item.icon && <span className="accordion-item-icon">{item.icon}</span>}
              <span className="accordion-trigger-label">{item.title}</span>
              <ChevronDown />
            </button>
            <div className="accordion-panel" role="region">
              <div className="accordion-panel-inner">
                <div className="accordion-content">{item.content}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
